import { Controller, useForm } from 'react-hook-form';
import { t } from '@app/i18n';
import { Field, FieldGrid, LoadingButton, SearchCombobox, TextInput } from '@zarya/ui';
import { TENANT_LABELS, tenantLabelText } from '@app/ambiente';
import { tenantDefaults, type TenantConfig, type TenantFormValues } from './TenantSettingsModel';
import { useTenantValidation } from './useFormValidation';
import s from './TenantSettings.module.css';

export interface TenantFormProps {
  tenant: TenantConfig;
  /** `false` para approver/viewer: campos em leitura, sem **Salvar** (FR-010). */
  editavel: boolean;
  pending?: boolean;
  onSubmit: (valores: TenantFormValues) => void;
}

/**
 * Função, não constante de módulo: uma lista de rótulos montada no import
 * congela o idioma da primeira pintura.
 */
const tenantLabelOptions = () =>
  TENANT_LABELS.map((valor) => ({ value: valor, label: tenantLabelText(valor) }));

/**
 * Campos do card Tenant (T21, FR-114, FR-115): Cliente, Ambiente e Trust domain.
 *
 * Sem campo Slug, sem "Componentes no cluster", sem "Aprovadores" — cortes do
 * PRD §4. O ambiente é um combobox (nada de `<select>` nativo) com as duas opções
 * do contrato.
 */
export function TenantForm({ tenant, editavel, pending = false, onSubmit }: TenantFormProps) {
  const { control, handleSubmit, formState } = useForm<TenantFormValues>({
    defaultValues: tenantDefaults(tenant),
    ...useTenantValidation(),
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label={t('tenantSettings.tenant.form.ariaLabel')}
    >
      <FieldGrid>
        <Controller
          control={control}
          name="cliente"
          render={({ field, fieldState }) => (
            <Field
              label={t('tenantSettings.tenant.form.client')}
              span={6}
              required
              error={fieldState.error?.message}
              tip={t('tenantSettings.tenant.form.client.tip')}
            >
              {(props) => <TextInput {...props} {...field} disabled={!editavel} />}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="label"
          render={({ field, fieldState }) => (
            <Field
              label={t('tenantSettings.tenant.form.label')}
              span={6}
              required
              error={fieldState.error?.message}
              tip={t('tenantSettings.tenant.form.label.tip')}
            >
              {(props) => (
                <SearchCombobox
                  id={props.id}
                  label={t('tenantSettings.tenant.form.label')}
                  options={tenantLabelOptions()}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? 'homologacao')}
                  disabled={!editavel}
                  invalid={Boolean(fieldState.error)}
                />
              )}
            </Field>
          )}
        />
      </FieldGrid>

      {editavel ? (
        <div className={s.acoes}>
          <LoadingButton type="submit" loading={pending} disabled={!formState.isValid}>
            {t('tenantSettings.tenant.form.save')}
          </LoadingButton>
        </div>
      ) : null}
    </form>
  );
}
